import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material';
import UbicacionSearch from '../UbicacionSearch';
import { IUbicacion } from '../../interfaces/Ubicacion';
import type { UbicacionUsuarioDraft } from '../../utils/syncUbicacionesUsuario';

interface UbicacionesUsuarioSectionProps {
  value: UbicacionUsuarioDraft[];
  onChange: (items: UbicacionUsuarioDraft[]) => void;
  disabled?: boolean;
}

const UbicacionesUsuarioSection: React.FC<UbicacionesUsuarioSectionProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const excludeNroUbicaciones = useMemo(
    () => value.map((u) => u.nroUbicacion),
    [value]
  );

  const handleAgregar = (ubicacion: IUbicacion) => {
    if (value.some((u) => u.nroUbicacion === ubicacion.nroUbicacion)) return;
    const nueva: UbicacionUsuarioDraft = {
      nroUbicacion: ubicacion.nroUbicacion,
      nombreUbicacion: ubicacion.nombreUbicacion?.trim() || `Ubicación ${ubicacion.nroUbicacion}`,
      preferida: value.length === 0,
    };
    onChange([...value, nueva]);
  };

  const handleEliminar = (nroUbicacion: number) => {
    const restantes = value.filter((u) => u.nroUbicacion !== nroUbicacion);
    if (restantes.length === 0) {
      onChange([]);
      return;
    }
    const eliminadaEraPreferida = value.find((u) => u.nroUbicacion === nroUbicacion)?.preferida;
    if (eliminadaEraPreferida && !restantes.some((u) => u.preferida)) {
      onChange(restantes.map((u, idx) => ({ ...u, preferida: idx === 0 })));
      return;
    }
    onChange(restantes);
  };

  const handleMarcarPreferida = (nroUbicacion: number) => {
    onChange(value.map((u) => ({ ...u, preferida: u.nroUbicacion === nroUbicacion })));
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Ubicaciones asignadas
        </Typography>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          disabled={disabled}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            py: 1,
            fontSize: '0.95rem',
          }}
        >
          Agregar ubicación
        </Button>
      </Stack>

      {value.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Sin ubicaciones asignadas. El usuario no podrá operar en depósitos hasta que se asigne al menos una.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Ubicación</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }} align="center">
                  Preferida
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 80 }} align="center">
                  Acción
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {value.map((item) => (
                <TableRow key={item.nroUbicacion}>
                  <TableCell>
                    {item.nombreUbicacion} ({item.nroUbicacion})
                    {item.preferida && (
                      <Chip label="Preferida" size="small" color="primary" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={item.preferida ? 'Ubicación preferida' : 'Marcar como preferida'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleMarcarPreferida(item.nroUbicacion)}
                          disabled={disabled || item.preferida}
                          sx={
                            item.preferida
                              ? {
                                  color: '#f9a825',
                                  '&.Mui-disabled': { color: '#f9a825' },
                                  '& .MuiSvgIcon-root': { color: '#f9a825' },
                                }
                              : undefined
                          }
                        >
                          {item.preferida ? (
                            <StarIcon fontSize="small" />
                          ) : (
                            <StarBorderIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Quitar asignación">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleEliminar(item.nroUbicacion)}
                          disabled={disabled}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UbicacionSearch
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSelect={handleAgregar}
        loadAllUbicaciones
        excludeNroUbicaciones={excludeNroUbicaciones}
      />
    </Box>
  );
};

export default UbicacionesUsuarioSection;
